import { makeObservable, observable, action, runInAction } from 'mobx';
import {
  PAYMENT_METHOD,
  RECIPIENT_TYPES,
  NEW_PAYMENT_CREATION_STEPS,
  BACKEND_MODULE_TYPES,
  NEW_PAYMENT_CREATION_STEPS_FOR_SWIFT,
  NEW_PAYMENT_CREATION_STEPS_FOR_CRYPTO_PAYMENT,
  NEW_PAYMENT_CREATION_STEPS_FOR_OTHER_PAYMENT_METHODS
} from 'components/common/constants';
import { setTimerDurationExpiresAt, updateConfirmationActionType } from 'services/authUtils';
import {
  validateTemplateAccountDetailsRequest,
  validateTemplateBankDetailsRequest,
  validateTemplateRecipientDetailsRequest,
  validateTemplateAdditionalRecipientDetailsRequest,
  validateTemplatePaymentDetailsRequest,
  transactionPaymentCheck,
  transactionCryptoCheck,
  transactionPaymentCreate,
  transactionCryptoCreate,
  transactionPaymentConfirm,
  transactionCryptoConfirm,
  resendSecurityCode,
  forceChangeConfirmationTypeRequest
} from 'services/requestAgent';
import CreateNewPaymentFormModel from 'components/NewPaymentFormV2/models/CreateNewPaymentFormModel';

class NewPaymentStore {
  isLoading = false;
  error = null;
  currentStepIndex = 0;
  newPaymentFormSteps = NEW_PAYMENT_CREATION_STEPS_FOR_OTHER_PAYMENT_METHODS;
  currentStep = NEW_PAYMENT_CREATION_STEPS.ACCOUNT_DETAILS;
  currentAccount = null;
  isShowingConfirmationTab = false;
  commission = null;
  serverTransactionData = null;
  transactionForCreate = null;
  isShowingInternalActionConfirmation = false;
  confirmationPopupError = null;
  confirmationId = null;
  isSuccess = false;
  isShowSwitchConfirmationMethodScreen = false;

  // Data objects for each step
  accountDetailsStep = {
    currency: '',
    accountNumber: '',
    iban: '',
    cryptoAddress: '',
    sortCode: '',
    network: '',
    isCryptoPayment: false,
    walletAddress: ''
  };

  bankDetailsStep = {
    bankName: '',
    bankCountry: '',
    bic: ''
  };

  paymentDetailsStep = {
    amount: '',
    comment: '',
    transferDetails: '',
    paymentMethod: ''
  };

  recipientDetailsStep = {
    recipientType: RECIPIENT_TYPES.COMPANY,
    companyName: '',
    firstName: '',
    lastName: ''
  };

  recipientAdditionalDetailsStep = {
    registrationNumber: '',
    country: '',
    city: '',
    postCode: '',
    address: ''
  };

  reviewAndSaveStep = {
    templateName: ''
  };

  // Combined data object for backward compatibility
  newPaymentFormData = {
    // Account Details
    currency: '',
    accountNumber: '',
    iban: '',
    cryptoAddress: '',
    sortCode: '',
    network: '',
    isCryptoPayment: false,
    walletAddress: '',

    // Bank Details
    bankName: '',
    bankCountry: '',
    bic: '',

    // Payment Details
    amount: '',
    comment: '',
    transferDetails: '',
    paymentMethod: '',

    // Recipient Details
    recipientType: RECIPIENT_TYPES.COMPANY,
    companyName: '',
    firstName: '',
    lastName: '',

    // Recipient Additional Details
    registrationNumber: '',
    country: '',
    city: '',
    postCode: '',
    address: '',

    // Review and Save
    templateName: ''
  };

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      error: observable,
      currentStepIndex: observable,
      newPaymentFormSteps: observable,
      currentStep: observable,
      currentAccount: observable,
      isShowingConfirmationTab: observable,
      commission: observable,
      serverTransactionData: observable,
      transactionForCreate: observable,
      isShowingInternalActionConfirmation: observable,
      confirmationPopupError: observable,
      confirmationId: observable,
      isSuccess: observable,
      isShowSwitchConfirmationMethodScreen: observable,
      accountDetailsStep: observable,
      bankDetailsStep: observable,
      paymentDetailsStep: observable,
      recipientDetailsStep: observable,
      recipientAdditionalDetailsStep: observable,
      reviewAndSaveStep: observable,
      newPaymentFormData: observable,

      setIsLoading: action,
      setError: action,
      goToNextStep: action,
      goToPrevStep: action,
      updateAccountDetailsStep: action,
      updateBankDetailsStep: action,
      updatePaymentDetailsStep: action,
      updateRecipientDetailsStep: action,
      updateRecipientAdditionalDetailsStep: action,
      updateNewPaymentFormData: action,
      resetNewPaymentFormCreationData: action,
      validateAccountDetails: action,
      validateBankDetails: action,
      validateRecipientDetails: action,
      validateAdditionalRecipientDetails: action,
      validatePaymentDetails: action,
      getAllStepsData: action,
      getCurrentStep: action,
      setSelectedAccount: action,
      setAccountInfo: action,
      setCurrentNewPaymentFormStep: action,
      validateTransfer: action,
      validateCryptoTransfer: action,
      setIsShowingConfirmationTab: action,
      resetCommissionData: action,
      createTransfer: action,
      createCryptoTransfer: action,
      verifyPaymentSecurityCode: action,
      verifyCryptoSecurityCode: action,
      resendPaymentSecurityCode: action,
      setIsShowingInternalActionConfirmation: action,
      clearConfirmationPopupError: action,
      resetSuccess: action,
      setIsShowSwitchConfirmationMethodScreen: action,
      forceSwitchToAnotherConfirmationType: action
    });
  }

  setIsLoading(value) {
    this.isLoading = value;
  }

  setError(error) {
    this.error = error;
  }

  getCurrentStepIndex() {
    return this.currentStepIndex;
  }

  getCurrentStep() {
    return this.newPaymentFormSteps[this.currentStepIndex];
  }

  goToNextStep() {
    this.currentStepIndex += 1;
  }

  goToPrevStep() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex -= 1;
    }
  }

  updateAccountDetailsStep(data) {
    this.accountDetailsStep = { ...this.accountDetailsStep, ...data };
    this.updateNewPaymentFormData(data);
  }

  updateBankDetailsStep(data) {
    this.bankDetailsStep = { ...this.bankDetailsStep, ...data };
    this.updateNewPaymentFormData(data);
  }

  updatePaymentDetailsStep(data) {
    this.paymentDetailsStep = { ...this.paymentDetailsStep, ...data };
    this.updateNewPaymentFormData(data);
  }

  updateRecipientDetailsStep(data) {
    this.recipientDetailsStep = { ...this.recipientDetailsStep, ...data };
    this.updateNewPaymentFormData(data);
  }

  updateRecipientAdditionalDetailsStep(data) {
    this.recipientAdditionalDetailsStep = { ...this.recipientAdditionalDetailsStep, ...data };
    this.updateNewPaymentFormData(data);
  }

  updateNewPaymentFormData(data) {
    this.newPaymentFormData = { ...this.newPaymentFormData, ...data };
  }

  resetNewPaymentFormCreationData() {
    this.currentStepIndex = 0;
    this.error = null;
    this.newPaymentFormSteps = NEW_PAYMENT_CREATION_STEPS_FOR_OTHER_PAYMENT_METHODS;

    this.accountDetailsStep = {
      currency: '',
      accountNumber: '',
      iban: '',
      cryptoAddress: '',
      sortCode: '',
      network: '',
      isCryptoPayment: false,
      walletAddress: ''
    };

    this.bankDetailsStep = {
      bankName: '',
      bankCountry: '',
      bic: ''
    };

    this.paymentDetailsStep = {
      amount: '',
      comment: '',
      transferDetails: '',
      paymentMethod: ''
    };

    this.recipientDetailsStep = {
      recipientType: RECIPIENT_TYPES.COMPANY,
      companyName: '',
      firstName: '',
      lastName: ''
    };

    this.recipientAdditionalDetailsStep = {
      registrationNumber: '',
      country: '',
      city: '',
      postCode: '',
      address: ''
    };

    this.reviewAndSaveStep = {
      templateName: ''
    };

    this.newPaymentFormData = {
      currency: '',
      accountNumber: '',
      iban: '',
      cryptoAddress: '',
      sortCode: '',
      network: '',
      isCryptoPayment: false,
      walletAddress: '',
      bankName: '',
      bankCountry: '',
      bic: '',
      amount: '',
      comment: '',
      transferDetails: '',
      paymentMethod: '',
      recipientType: RECIPIENT_TYPES.COMPANY,
      companyName: '',
      firstName: '',
      lastName: '',
      registrationNumber: '',
      country: '',
      city: '',
      postCode: '',
      address: '',
      templateName: ''
    };
  }

  // Validation methods copied from paymentStore
  async validateAccountDetails(customerNumber, data, isCryptoPayment = false) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplateAccountDetailsRequest(customerNumber, data);

      const currentData = { ...this.newPaymentFormData };

      if (response.paymentMethod === PAYMENT_METHOD.INTERNAL) {
        if (response.network) {
          response.cryptoNetwork = response.network;
          response.cryptoAddress = response.accountNumber;
          response.accountNumber = response.internalWalletNumber;
        } else {
          if (response.internalWalletNumber) {
            response.accountNumber = response.internalWalletNumber;
          } else if (response?.accountNumber?.length > 8) {
            response.iban = response.accountNumber;
          } else {
            response.iban = '';
          }
        }
      } else if (response.paymentMethod === PAYMENT_METHOD.CRYPTO && response.network) {
        response.cryptoNetwork = response.network;
        response.cryptoAddress = response.accountNumber;
        response.accountNumber = '';
      }

      if (!isCryptoPayment) {
        currentData.cryptoNetwork = '';
        currentData.cryptoAddress = '';
      }

      const mergedData = {
        ...currentData,
        ...Object.fromEntries(
          Object.entries(response).map(([key, value]) => [key, value === null ? currentData[key] : value])
        )
      };

      runInAction(() => {
        this.error = null;
        this.newPaymentFormData = mergedData;
        this.accountDetailsStep = { ...this.accountDetailsStep, ...response };
        this.bankDetailsStep = { ...this.bankDetailsStep, ...response };
        this.paymentDetailsStep = { ...this.paymentDetailsStep, paymentMethod: response.paymentMethod };
        this.newPaymentFormSteps = isCryptoPayment
          ? NEW_PAYMENT_CREATION_STEPS_FOR_CRYPTO_PAYMENT
          : response.paymentMethod === PAYMENT_METHOD.SWIFT || response.paymentMethod === PAYMENT_METHOD.TARGET2
            ? NEW_PAYMENT_CREATION_STEPS_FOR_SWIFT
            : NEW_PAYMENT_CREATION_STEPS_FOR_OTHER_PAYMENT_METHODS;
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateBankDetails(customerNumber, data) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplateBankDetailsRequest(customerNumber, data);

      runInAction(() => {
        this.error = null;
        this.newPaymentFormData = { ...this.newPaymentFormData, ...response };
        this.bankDetailsStep = { ...this.bankDetailsStep, ...response };
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateRecipientDetails(customerNumber, data) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplateRecipientDetailsRequest(customerNumber, data);

      runInAction(() => {
        this.error = null;
        this.newPaymentFormData = { ...this.newPaymentFormData, ...response };
        this.recipientDetailsStep = { ...this.recipientDetailsStep, ...response };
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validateAdditionalRecipientDetails(customerNumber, data) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplateAdditionalRecipientDetailsRequest(customerNumber, data);

      runInAction(() => {
        this.error = null;
        this.newPaymentFormData = { ...this.newPaymentFormData, ...response };
        this.recipientAdditionalDetailsStep = { ...this.recipientAdditionalDetailsStep, ...response };
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async validatePaymentDetails(customerNumber, data) {
    try {
      this.setIsLoading(true);

      const response = await validateTemplatePaymentDetailsRequest(customerNumber, data);

      runInAction(() => {
        this.error = null;
        this.newPaymentFormData = { ...this.newPaymentFormData, ...response };
        this.paymentDetailsStep = { ...this.paymentDetailsStep, ...response };
      });

      this.goToNextStep();
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  getAllStepsData() {
    return {
      ...this.accountDetailsStep,
      ...this.bankDetailsStep,
      ...this.paymentDetailsStep,
      ...this.recipientDetailsStep,
      ...this.recipientAdditionalDetailsStep,
      ...this.reviewAndSaveStep
    };
  }

  setSelectedAccount(account) {
    this.currentAccount = account;
  }

  setAccountInfo(account) {
    this.setSelectedAccount(account);

    // Update account details step with selected account info
    this.updateAccountDetailsStep({
      currency: account?.currency || '',
      network: account?.network || '',
      walletNumber: account?.wallet_number || ''
    });
  }

  setCurrentNewPaymentFormStep(step) {
    const stepIndex = this.newPaymentFormSteps.indexOf(step);
    if (stepIndex !== -1) {
      this.currentStepIndex = stepIndex;
      this.currentStep = step;
    }
  }

  setIsShowingConfirmationTab(value) {
    this.isShowingConfirmationTab = value;
  }

  resetCommissionData() {
    this.isShowingConfirmationTab = false;
    this.commission = null;
    this.serverTransactionData = null;
    this.transactionForCreate = null;
    this.error = null;
  }

  async validateTransfer({ customerNumber, providerData }) {
    this.setIsLoading(true);
    const data = CreateNewPaymentFormModel({
      providerTableData: providerData,
      data: {
        wallet: this.currentAccount,
        paymentMethod: this.paymentDetailsStep?.paymentMethod,
        uploadedFiles: [],
        ...this.getAllStepsData()
      }
    });

    try {
      const transactionData = await transactionPaymentCheck(customerNumber, data);

      runInAction(() => {
        this.isLoading = false;
        this.error = null;
        this.isShowingConfirmationTab = true;
        this.paymentDetailsStep = { ...this.paymentDetailsStep, paymentMethod: transactionData.payment_method };
        this.transactionForCreate = {
          ...data,
          source_amount: transactionData.source_amount,
          target_amount: transactionData.target_amount
        };
        this.serverTransactionData = transactionData;
        this.commission = {
          value: transactionData.total_commissions,
          currency: transactionData.currency_code,
          type: transactionData.commission_type
        };
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
      throw err;
    }
  }

  async validateCryptoTransfer({ customerNumber, providerData }) {
    this.setIsLoading(true);
    const data = CreateNewPaymentFormModel({
      providerTableData: providerData,
      data: {
        wallet: this.currentAccount,
        paymentMethod: this.paymentDetailsStep?.paymentMethod,
        uploadedFiles: [],
        ...this.getAllStepsData()
      }
    });

    try {
      const transactionData = await transactionCryptoCheck(customerNumber, data);

      runInAction(() => {
        this.isLoading = false;
        this.error = null;
        this.isShowingConfirmationTab = true;
        this.paymentDetailsStep = { ...this.paymentDetailsStep, paymentMethod: transactionData.payment_method };
        this.transactionForCreate = {
          ...data,
          source_amount: transactionData.source_amount,
          target_amount: transactionData.target_amount
        };
        this.serverTransactionData = transactionData;
        this.commission = {
          value: transactionData.total_commissions,
          currency: transactionData.currency_code,
          type: transactionData.commission_type
        };
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
      throw err;
    }
  }

  createTransfer(customerNumber) {
    return async () => {
      this.setIsLoading(true);
      try {
        const {
          confirmation_id: confirmationId,
          confirmation_type: confirmationType,
          expires_at: expiresAt
        } = await transactionPaymentCreate(customerNumber, {
          ...this.transactionForCreate
        });

        setTimerDurationExpiresAt(expiresAt);
        updateConfirmationActionType(confirmationType);

        runInAction(() => {
          this.isLoading = false;
          this.error = null;
          this.confirmationId = confirmationId;
          this.isShowingInternalActionConfirmation = true;
        });
      } catch (err) {
        runInAction(() => {
          this.isLoading = false;
          this.error = err;
        });
      }
    };
  }

  createCryptoTransfer(customerNumber) {
    return async () => {
      this.setIsLoading(true);
      try {
        const {
          confirmation_id: confirmationId,
          confirmation_type: confirmationType,
          expires_at: expiresAt
        } = await transactionCryptoCreate(customerNumber, {
          ...this.transactionForCreate
        });

        setTimerDurationExpiresAt(expiresAt);
        updateConfirmationActionType(confirmationType);

        runInAction(() => {
          this.isLoading = false;
          this.error = null;
          this.isShowingInternalActionConfirmation = true;
          this.confirmationId = confirmationId;
        });
      } catch (err) {
        runInAction(() => {
          this.isLoading = false;
          this.error = err;
        });
      }
    };
  }

  async verifyPaymentSecurityCode(customerNumber, securityCode) {
    try {
      this.setIsLoading(true);
      await transactionPaymentConfirm(customerNumber, {
        code: securityCode,
        confirmation_id: this.confirmationId
      });

      runInAction(() => {
        this.isLoading = false;
        this.error = null;
        this.confirmationPopupError = null;
        this.isShowingConfirmationTab = false;
        this.isShowingInternalActionConfirmation = false;
        this.isSuccess = true;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.confirmationPopupError = err;
      });
    }
  }

  async verifyCryptoSecurityCode(customerNumber, securityCode) {
    try {
      this.setIsLoading(true);
      await transactionCryptoConfirm(customerNumber, {
        code: securityCode,
        confirmation_id: this.confirmationId
      });

      runInAction(() => {
        this.isLoading = false;
        this.error = null;
        this.confirmationPopupError = null;
        this.isShowingConfirmationTab = false;
        this.isShowingInternalActionConfirmation = false;
        this.isSuccess = true;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.confirmationPopupError = err;
      });
    }
  }

  async resendPaymentSecurityCode() {
    try {
      runInAction(() => {
        this.isLoading = true;
      });
      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await resendSecurityCode(BACKEND_MODULE_TYPES.PAYMENT, this.confirmationId);

      updateConfirmationActionType(confirmationType);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationPopupError = null;
        this.confirmationId = confirmationId;
        this.isShowingInternalActionConfirmation = true;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await forceChangeConfirmationTypeRequest(this.confirmationId, confirmationType, BACKEND_MODULE_TYPES.PAYMENT);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };

  setIsShowingInternalActionConfirmation(status) {
    this.isShowingInternalActionConfirmation = status;
  }

  clearConfirmationPopupError() {
    this.confirmationPopupError = null;
  }

  resetSuccess() {
    this.isSuccess = false;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }
}

export default new NewPaymentStore();
